// External Imports
import { useState } from "react";
import { MdClose } from "react-icons/md";

// Internal Imports
import { requestAdminAPI } from "../../api/posh-api";
import Heading from "../UI/Heading";
import VehicleSelector from "../vehicles-page/VehicleSelector";
import DetailPair from "../UI/DetailPair";
import LinkPair from "../UI/LinkPair";
import SelectPair from "../UI/SelectPair";
import LoadingSpinner from "../UI/LoadingSpinner";
import { uploadImageToFirebase } from "../../util/upload-image-to-firebase";
import Modal from "../UI/Modal";

/** ========================================================================
 * * AddMaintenance Component
 * This component renders a form to add a maintenance log.
 * ========================================================================== */

export default function AddMaintenance({
  open,
  setOpen,
  vehicleId,
  onComplete,
}) {
  // Initialize States
  const [vehicle, setVehicle] = useState();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  /* ======================= SELECT VEHICLE ======================= */
  const selectVehicle = async (vehicle) => {
    try {
      setLoading(true);
      /*
       * Vehicle report includes mileage,
       * which is auto-filled in the form fields.
       */
      const response = await requestAdminAPI("fetch-vehicle-reports", {
        vehicles: [{ vin: vehicle.vin, trackerId: vehicle.trackerId }],
      });
      const report = response[0]?.report;

      // Update the form fields with the vehicle's report data
      if (report)
        document.getElementsByName("startMileage")[0].value = report.mileage;

      setVehicle(vehicle);
      setLoading(false);
    } catch (err) {
      setVehicle(vehicle);
      setLoading(false);
      // Reset the form fields
      document.getElementsByName("startMileage")[0].value = "";
    }
  };

  /* ======================= ADD MAINTENANCE ======================= */
  const submitHandler = async () => {
    try {
      setLoading(true);
      if (!vehicle) throw new Error("Please select a vehicle.");

      // Create a payload for all maintenance information
      const maintenance = {
        vehicleId: vehicle.id,
        description: document.getElementById("description").value,
        priority: document.getElementById("priority").value,
        unavailable: document.getElementById("unavailable").value === "yes",
        startMileage: document.getElementById("startMileage").value,
      };

      // Upload Image
      const image = document.getElementById("image").files[0];
      const imageUrl = image
        ? await uploadImageToFirebase(
            `vehicles/${vehicle.id}/maintenance/${new Date().toISOString()}`,
            "Image",
            image,
            setUploading
          )
        : null;
      if (imageUrl) maintenance.image = imageUrl;

      // Add Maintenance
      const res = await requestAdminAPI("add-maintenance", maintenance);
      onComplete(res);

      setLoading(false);
      setOpen(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal lg:w-[60vw]">
        <Heading
          title="Report Issue"
          text="Adding a log sends a notification on slack maintenace channel and places the log in the reported status by default."
          isBackArrow={false}
        >
          <MdClose className="text-2xl icon" onClick={() => setOpen(false)} />
        </Heading>
        <div className="info lg:grid-cols-2">
          <VehicleSelector
            selected={vehicleId || false}
            onSelect={selectVehicle}
          />
          <DetailPair
            label="CURRENT MILEAGE"
            edit={true}
            inputOptions={{ id: "startMileage", required: true }}
          />
          <SelectPair
            label="PRIORITY"
            options={[
              { id: 0, name: "Low" },
              { id: 1, name: "Medium" },
              { id: 2, name: "High" },
            ]}
            edit={true}
            inputOptions={{
              id: "priority",
            }}
          />
          <SelectPair
            label="VEHICLE AVAILABLITY"
            options={[
              { id: "no", name: "Do not change" },
              { id: "yes", name: "Mark as unavailable" },
            ]}
            edit={true}
            inputOptions={{
              id: "unavailable",
            }}
          />
          <div className="md:col-span-2">
            <label className="text-sm text-stone-400">DESCRIPTION:</label>
            <textarea
              id="description"
              className="w-full border input border-stone-600"
              rows="4"
              required
            />
          </div>
          <LinkPair
            label="IMAGE OF ISSUE"
            edit={true}
            inputOptions={{
              type: "file",
              id: "image",
            }}
          />
          {loading ? (
            <div className="flex items-center self-end space-x-4">
              <LoadingSpinner />
              {uploading && (
                <p className="text-sm text-stone-400">
                  Uploading {uploading[0]}... {uploading[1]}%
                </p>
              )}
            </div>
          ) : (
            <div onClick={submitHandler} className="self-end btn-primary">
              Submit
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
